import type { BookingState } from "./bookingState";

/**
 * Which of **four outcomes** a person is shown for a booking's effective state
 * (docs/domain/03-core-model.md §2).
 *
 * **The state machine is unchanged**: Booking still has all eight states and
 * the backend still distinguishes them. What this decides is how many of them a
 * reader is made to parse, and the answer is four:
 *
 *   requested  -> awaiting    (nothing has decided yet)
 *   approved   -> confirmed   (the positive path)
 *   completed  -> completed   (history)
 *   everything else -> cancelled
 *
 * "Expired", "Withdrawn", "Declined" and "Cancelled by business" all collapse
 * into **cancelled**. From where the reader sits the appointment is not
 * happening, and the difference between "the clinic never answered" and "the
 * clinic said no" is not something to make them work out. Anyone who needs the
 * real state (support, the clinic's own records) reads it from the data.
 *
 * **It lives in the domain, moved here in R22 T9**, because the collapse is a
 * rule rather than a rendering: it ships from the server so no client can
 * quietly grow a fifth outcome. It sat in `app/_components` while the web was
 * the only reader, which stopped being true when the mobile wire format started
 * importing it, and stopped being *possible* when the API Worker did. What
 * stays with each client is the **words** (and the web's colours), so a new
 * locale is a client change rather than a server one, which is what the message
 * catalogue of `05-build-plan.md` R11 needs.
 */

export type StatusKey = "awaiting" | "confirmed" | "completed" | "cancelled";

/**
 * Two hues and a neutral, no more. Confirmed is the accent, cancelled is
 * danger, completed is neutral, and pending is an outline rather than a colour:
 * it is the absence of a decision, so it should not compete with one.
 */
export type StatusTone = "success" | "danger" | "warning" | "info" | "muted";

export interface BookingStatusCollapse {
  key: StatusKey;
  tone: StatusTone;
}

export function collapseBookingStatus(
  state: BookingState,
): BookingStatusCollapse {
  switch (state) {
    case "requested":
      return { key: "awaiting", tone: "warning" };
    case "approved":
      // The accent rather than green by design: the accent is the product's
      // interactive colour, so confirming reads as the positive path without a
      // green competing with the brand.
      return { key: "confirmed", tone: "info" };
    case "completed":
      return { key: "completed", tone: "muted" };
    case "declined":
    case "expired":
    case "withdrawn":
    case "cancelled_by_consumer":
    case "cancelled_by_business":
      return { key: "cancelled", tone: "danger" };
  }
}
